import { type FillMethod, isReauthRequired } from '@felix/domain';
import { Button } from '@mantine/core';
import {
  IconAppWindow,
  IconCircleCheck,
  IconDownload,
  IconExternalLink,
  IconForms,
  IconInfoCircle,
  IconPaperclip,
  IconUpload,
} from '@tabler/icons-react';
import { useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ApprovalPolicyContext } from '../../approval-policy.js';
import { Panel } from '../../Panel.js';
import { type FillStep, FillSteps } from './FillSteps.js';
import classes from './Forms.module.css';
import { officeFileOf } from './record-model.js';
import type { DraftFile } from './types.js';

export interface RecordGuideProps {
  fillMethod: FillMethod;
  /** Taslağın dosyaları: durum satırı ve tamam adımlar. */
  files: readonly DraftFile[];
  /** Süren işlem (`open`, `attach`, `upload`, `template`, `fill`): o düğme yükleniyor. */
  busy: string | null;
  /** Form Felixmate Office Pack'te açık (kapanınca dosya kendiliğinden eklenir; ADR 0070 §8). */
  officeOpen?: boolean | undefined;
  /** Office yolu: formu Office'te aç (ilk kez ya da yeniden; açıksa pencere öne gelir). */
  onFillIn?: (() => void) | undefined;
  /** Office yolu: kendiliğinden eklenmediyse kaydedilen dosyayı elle ekle (ADR 0070 §10). */
  onAttach?: (() => void) | undefined;
  /** PDF yolu: uygulamada doldur (ADR 0055). */
  onFillInApp?: (() => void) | undefined;
  /** Uygulamada doldurma kapalıysa nedeni (ör. dosya gemide değil). */
  fillInAppBlocked?: string | null | undefined;
  /** Taranmış ya da fotoğraflanmış kâğıt; ekran Office dosyası ekliyse önce sorar. */
  onUpload: () => void;
  /** Şablonu indir (kâğıt yolu; revizyonun dosyası gemide ise). */
  onDownloadTemplate?: (() => void) | undefined;
}

/**
 * Kaydı tamamlama akışı (sahip kararı 2026-10-17; ADR 0074). Dört adımlı uzun kutunun ve büyük
 * yükleme alanının yerine doldurma yöntemine göre kısa 1–2–3:
 * - Office (Word/Excel): Fill in → Save and close Office (dosya kendiliğinden eklenir) → Submit;
 *   Office açıkken belirgin durum şeridi ve "Bring Office to front"; kâğıt taraması ikincil ve
 *   Office açıkken kapalı.
 * - Kâğıt (PDF, ıslak imza): Fill in and sign → Upload scan / photo (birincil) → Submit.
 * - Uygulamada PDF: Fill in app → Check the file → Submit; tarama ikincil.
 * Altta durum satırı ("Filled file attached from Office ✓"). Gönder düğmesi sayfanın altındadır.
 */
export function RecordGuide({
  fillMethod,
  files,
  busy,
  officeOpen = false,
  onFillIn,
  onAttach,
  onFillInApp,
  fillInAppBlocked = null,
  onUpload,
  onDownloadTemplate,
}: RecordGuideProps) {
  const { t } = useTranslation();
  const acceptedId = useId();
  const blockedId = useId();
  // ISM formunda gönderme açık adımla; parola yalnız şirket ayarı isterse (ADR 0056)
  const password = isReauthRequired('ism_form', useContext(ApprovalPolicyContext));
  const officeFile = officeFileOf(fillMethod, files);
  const filled =
    fillMethod === 'office'
      ? officeFile !== null
      : fillMethod === 'pdf_form'
        ? files.some((f) => f.source === 'pdf_form')
        : files.length > 0;
  const lock = (key: string) => busy !== null && busy !== key;
  const submitStep: FillStep = {
    title: t('forms.howTo.step3'),
    text: t(password ? 'forms.guide.step4TextPassword' : 'forms.guide.step4Text'),
    state: filled ? 'current' : undefined,
  };
  const accepted = (
    <p id={acceptedId} className={classes.howToAccepted}>
      {t('forms.guide.accepted')}
    </p>
  );
  const uploadButton = (primary: boolean) => (
    <Button
      size="xs"
      // Dosya eklenince vurgu Submit'e geçer: yükleme düğmesi sakinleşir
      variant={primary ? (filled ? 'default' : 'filled') : 'subtle'}
      className={primary ? classes.fillAction : undefined}
      leftSection={<IconUpload size={14} />}
      loading={busy === 'upload'}
      disabled={officeOpen || lock('upload')}
      aria-describedby={officeOpen ? `${blockedId} ${acceptedId}` : acceptedId}
      onClick={onUpload}
      data-action="upload"
    >
      {t('forms.howTo.upload')}
    </Button>
  );

  let steps: FillStep[];
  if (fillMethod === 'office') {
    steps = [
      {
        title: t('forms.howTo.office.step1'),
        text: t('forms.howTo.office.step1Text'),
        state: filled ? 'done' : officeOpen ? undefined : 'current',
        children: onFillIn ? (
          <Button
            size="xs"
            variant={filled ? 'default' : 'filled'}
            className={classes.fillAction}
            leftSection={<IconExternalLink size={14} />}
            loading={busy === 'open'}
            disabled={lock('open')}
            onClick={onFillIn}
            data-action="open"
          >
            {t(filled ? 'forms.howTo.office.reopen' : 'forms.howTo.office.step1')}
          </Button>
        ) : null,
      },
      {
        title: t('forms.howTo.office.step2'),
        text: t('forms.howTo.office.step2Text'),
        state: filled ? 'done' : officeOpen ? 'current' : undefined,
        children: onAttach ? (
          <Button
            size="compact-xs"
            variant="subtle"
            className={classes.fillLink}
            leftSection={<IconPaperclip size={13} />}
            loading={busy === 'attach'}
            disabled={lock('attach')}
            onClick={onAttach}
            data-action="attach"
          >
            {t('forms.howTo.office.attach')}
          </Button>
        ) : null,
      },
      submitStep,
    ];
  } else if (fillMethod === 'pdf_form') {
    steps = [
      {
        title: t('forms.howTo.pdf_form.step1'),
        text: t('forms.howTo.pdf_form.step1Text'),
        state: filled ? 'done' : 'current',
        children: onFillInApp ? (
          <Button
            size="xs"
            variant={filled ? 'default' : 'filled'}
            className={classes.fillAction}
            leftSection={<IconForms size={14} />}
            loading={busy === 'fill'}
            disabled={Boolean(fillInAppBlocked) || lock('fill')}
            title={fillInAppBlocked ?? undefined}
            onClick={onFillInApp}
            data-action="fill"
          >
            {t('forms.howTo.pdf_form.step1')}
          </Button>
        ) : null,
      },
      {
        title: t('forms.howTo.pdf_form.step2'),
        text: t('forms.howTo.pdf_form.step2Text'),
        state: filled ? 'done' : undefined,
      },
      submitStep,
    ];
  } else {
    steps = [
      {
        title: t('forms.howTo.print_scan.step1'),
        text: t('forms.howTo.print_scan.step1Text'),
        state: filled ? 'done' : undefined,
        children: onDownloadTemplate ? (
          <Button
            size="compact-xs"
            variant="subtle"
            className={classes.fillLink}
            leftSection={<IconDownload size={13} />}
            loading={busy === 'template'}
            disabled={lock('template')}
            onClick={onDownloadTemplate}
            data-action="template"
          >
            {t('forms.guide.downloadTemplate')}
          </Button>
        ) : null,
      },
      {
        title: t('forms.howTo.print_scan.step2'),
        text: t('forms.howTo.print_scan.step2Text'),
        state: filled ? 'done' : 'current',
        children: (
          <>
            {uploadButton(true)}
            {accepted}
          </>
        ),
      },
      submitStep,
    ];
  }

  const status =
    fillMethod === 'office' ? (
      officeFile ? (
        <>
          {t('forms.howTo.status.attachedOffice')}{' '}
          <span className={classes.howToStatusName}>{officeFile.name}</span>
        </>
      ) : (
        t('forms.howTo.status.waitingOffice')
      )
    ) : files.length > 0 ? (
      t('forms.howTo.status.files', { count: files.length })
    ) : (
      t('forms.howTo.status.none')
    );

  return (
    <Panel title={t('forms.howTo.title')} className={classes.howToPanel}>
      {officeOpen ? (
        <div className={classes.officeOpen} role="status" data-office-open>
          <IconAppWindow size={20} aria-hidden="true" />
          <p className={classes.officeOpenText}>{t('forms.howTo.officeOpen')}</p>
          {onFillIn ? (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconExternalLink size={14} />}
              loading={busy === 'open'}
              disabled={lock('open')}
              onClick={onFillIn}
              data-action="front"
            >
              {t('forms.howTo.front')}
            </Button>
          ) : null}
        </div>
      ) : null}
      <FillSteps steps={steps} layout="horizontal" />
      <p className={classes.howToStatus} data-tone={filled ? 'success' : undefined} role="status">
        {filled ? (
          <IconCircleCheck size={16} aria-hidden="true" />
        ) : (
          <IconInfoCircle size={16} aria-hidden="true" />
        )}
        <span>{status}</span>
      </p>
      {fillMethod !== 'print_scan' ? (
        // Kâğıt ikinci yoldur: Office ile doldurulmuş dosya ekliyse ekran önce sorar
        <div className={classes.howToPaper}>
          <p className={classes.howToPaperLabel}>{t('forms.howTo.onPaper')}</p>
          {uploadButton(false)}
          {officeOpen ? (
            <p id={blockedId} className={classes.howToAccepted}>
              {t('forms.howTo.closeOfficeFirst')}
            </p>
          ) : null}
          {accepted}
        </div>
      ) : null}
    </Panel>
  );
}
